import { motion, useReducedMotion, type Transition } from "framer-motion";
import Link from "next/link";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type Ref,
  type RefObject,
} from "react";

import CartChip from "@/blueprint/components/NavPill/CartChip";
import {
  MotionDuration,
  MotionTransition,
  NAV_PANEL_TRIGGER_EASE_CSS,
} from "@/blueprint/tokens/Motion";
import {
  NAV_LINKS,
  type NavHoverPanelId,
  SHOP_HREF,
  SHOP_LABEL,
} from "@/chrome/chromeContent";
import NavHoverPanel, {
  type NavPanelLayout,
} from "@/chrome/SiteNav/NavHoverPanel";
import { cn } from "@/lib/utils";
import { useTrack, type NavPanel, type NavSurface } from "@/analytics/events";

/** Matches SiteNav's NAV_OPEN_DELAY_MS: hover has to settle to count. */
const NAV_DWELL_MS = 120;

const CTA_CLASSES =
  "rounded-full bg-content-primary font-ppMori text-nav-label font-semibold text-content-inverse transition-colors duration-quick hover:bg-brand-blue motion-reduce:transition-none";

export const DESKTOP_NAV_CART_WIDTH_PX = 50;

/** Ignore hover opens for this long after a collapse swap. */
const NAV_SETTLE_MS = 400;

const NAV_CLOSE_DELAY_MS = 120;
const NAV_EASE_OUT: [number, number, number, number] = [
  ...MotionTransition.navHover.ease,
];
const PANEL_LEFT_OVERHANG_PX = 8;
const PANEL_TOP_OVERHANG_PX = 32;
const PANEL_RIGHT_OVERHANG_PX = 28;

type HoverSurfaceGeometry = NavPanelLayout & {
  height: number;
};

const HIDDEN_SURFACE_GEOMETRY: HoverSurfaceGeometry = {
  left: 0,
  top: 0,
  width: 0,
  height: 0,
};

const geometriesMatch = (
  first: HoverSurfaceGeometry,
  second: HoverSurfaceGeometry,
) =>
  first.left === second.left &&
  first.top === second.top &&
  first.width === second.width &&
  first.height === second.height;

const panelLayoutsMatch = (
  first: NavPanelLayout | null,
  second: NavPanelLayout,
) =>
  first?.left === second.left &&
  first.top === second.top &&
  first.width === second.width;

const assignRef = <T,>(ref: Ref<T> | undefined, value: T | null) => {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref !== null && ref !== undefined) {
    ref.current = value;
  }
};

type DesktopNavRightControlsOptions = {
  cartCount: number;
  onCartClick: () => void;
};

type DesktopNavLinksComposition = {
  links: ReactNode;
  rightControls: (options: DesktopNavRightControlsOptions) => ReactNode;
  /**
   * The hovered-link chip, for surfaces that already carry their own container.
   * The main hover surface is a sibling of the whole composition because it has
   * to grow into the hover panel, which is far bigger than any container — so
   * inside an opaque one it is both occluded and white-on-white. Place this
   * where it can paint: above the container's own backdrop, below the links.
   */
  linkHighlight: ReactNode;
};

type DesktopNavLinksProps = {
  children: (composition: DesktopNavLinksComposition) => ReactNode;
  containerRef?: Ref<HTMLDivElement>;
  onNavigate?: (() => void) | undefined;
  collapsed?: boolean;
  settleAfterCollapseChange: boolean;
  onDismiss?: () => void;
  onEscape?: () => void;
};

type DesktopNavRightControlsProps = DesktopNavRightControlsOptions & {
  panel: NavHoverPanelId | null;
  shopTriggerRef: RefObject<HTMLAnchorElement | null>;
  onShopEnter: () => void;
  onShopLeave: () => void;
  onShopKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  onNavigate: () => void;
  onCartEnter: () => void;
  onControlsFocus: (event: FocusEvent<HTMLDivElement>) => void;
};

const DesktopNavRightControls = ({
  cartCount,
  onCartClick,
  panel,
  shopTriggerRef,
  onShopEnter,
  onShopLeave,
  onShopKeyDown,
  onNavigate,
  onCartEnter,
  onControlsFocus,
}: DesktopNavRightControlsProps) => {
  const [cartVisualPresent, setCartVisualPresent] = useState(cartCount > 0);
  const reducedMotion = useReducedMotion() === true;
  const cartTransition: Transition = reducedMotion
    ? { duration: 0 }
    : {
        duration: MotionDuration.quickMs / 1000,
        ease: NAV_EASE_OUT,
      };

  useLayoutEffect(() => {
    if (cartCount > 0) setCartVisualPresent(true);
  }, [cartCount]);

  return (
    <div className="flex items-center gap-1" onFocus={onControlsFocus}>
      <Link
        ref={shopTriggerRef}
        href={SHOP_HREF}
        aria-expanded={panel === "shop"}
        aria-haspopup="true"
        data-nav-control="shop"
        onMouseEnter={onShopEnter}
        onMouseLeave={onShopLeave}
        /* Deliberately no onFocus open. SiteNav re-applies focus to this
           trigger every time the nav swaps between its in-flow and floating
           forms, and that arrives after the swap animation — so any focus
           open re-opens the panel on every scroll-up with no user intent.
           Hover opens it; the trigger itself still navigates to the shop. */
        onKeyDown={onShopKeyDown}
        onClick={onNavigate}
        className={cn(
          CTA_CLASSES,
          "px-l py-m",
          panel === "shop" && "bg-brand-blue",
        )}
      >
        {SHOP_LABEL}
      </Link>
      <motion.div
        onMouseEnter={onCartEnter}
        initial={false}
        animate={{ width: cartCount > 0 ? DESKTOP_NAV_CART_WIDTH_PX : 0 }}
        transition={cartTransition}
        onAnimationComplete={() => {
          if (cartCount === 0) setCartVisualPresent(false);
        }}
        className="shrink-0 overflow-hidden"
      >
        {/* Unmounts once the collapse animation ends: an empty cart must not
            remain an invisible tab stop. It stays mounted through the width
            animation because cartVisualPresent only flips on completion. */}
        {cartVisualPresent && (
          <CartChip
            count={cartCount}
            onClick={onCartClick}
            navControl="cart"
            className="size-[50px]"
          />
        )}
      </motion.div>
    </div>
  );
};

export const DesktopNavLinks = ({
  children,
  containerRef,
  onNavigate,
  collapsed = false,
  settleAfterCollapseChange,
  onDismiss,
  onEscape,
}: DesktopNavLinksProps) => {
  const track = useTrack();
  const panelDwellRef = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(panelDwellRef.current), []);
  const [panel, setPanel] = useState<NavHoverPanelId | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [surfaceGeometry, setSurfaceGeometry] = useState<HoverSurfaceGeometry>(
    HIDDEN_SURFACE_GEOMETRY,
  );
  const [panelLayout, setPanelLayout] = useState<NavPanelLayout | null>(null);
  const [linkHighlightGeometry, setLinkHighlightGeometry] =
    useState<HoverSurfaceGeometry>(HIDDEN_SURFACE_GEOMETRY);
  const linkHighlightRef = useRef<HTMLDivElement>(null);
  const [panelHeights, setPanelHeights] = useState<
    Partial<Record<NavHoverPanelId, number>>
  >({});
  const rootRef = useRef<HTMLDivElement>(null);
  const linksRowRef = useRef<HTMLDivElement>(null);
  const hoveredIndexRef = useRef<number | null>(null);
  const activePanelRef = useRef<NavHoverPanelId | null>(null);
  /** Blocks hover opens after a caller-identified collapse swap. */
  const collapseSettledAtRef = useRef(0);
  /** Whether the pointer is resting on the Shop trigger right now. */
  const shopHoveredRef = useRef(false);
  const lastPanelRef = useRef<NavHoverPanelId>("resources");
  const resourcesTriggerRef = useRef<HTMLButtonElement>(null);
  const shopTriggerRef = useRef<HTMLAnchorElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const reducedMotion = useReducedMotion() === true;
  const surfaceTransition: Transition = reducedMotion
    ? { duration: 0 }
    : {
        duration: MotionTransition.navMorph.durationMs / 1000,
        ease: [...MotionTransition.navMorph.ease],
      };

  const setRootRef = useCallback(
    (node: HTMLDivElement | null) => {
      rootRef.current = node;
      assignRef(containerRef, node);
    },
    [containerRef],
  );

  const updateHoveredIndex = useCallback((nextIndex: number | null) => {
    hoveredIndexRef.current = nextIndex;
    setHoveredIndex(nextIndex);
  }, []);

  const updateSurfaceGeometry = useCallback(
    (nextGeometry: HoverSurfaceGeometry) => {
      setSurfaceGeometry((current) =>
        geometriesMatch(current, nextGeometry) ? current : nextGeometry,
      );
    },
    [],
  );

  const getGeometryWithin = useCallback(
    (element: HTMLElement, origin: HTMLElement): HoverSurfaceGeometry => {
      const originRect = origin.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      return {
        left: Math.round(elementRect.left - originRect.left),
        top: Math.round(elementRect.top - originRect.top),
        width: Math.round(elementRect.width),
        height: Math.round(elementRect.height),
      };
    },
    [],
  );

  const getElementSurfaceGeometry = useCallback(
    (element: HTMLElement): HoverSurfaceGeometry | null => {
      const root = rootRef.current;
      return root === null ? null : getGeometryWithin(element, root);
    },
    [getGeometryWithin],
  );

  const getLinkSurfaceGeometry = useCallback(
    (index: number): HoverSurfaceGeometry | null => {
      const link = linksRowRef.current?.querySelector<HTMLElement>(
        `[data-nav-link-index="${index}"]`,
      );
      return link === null || link === undefined
        ? null
        : getElementSurfaceGeometry(link);
    },
    [getElementSurfaceGeometry],
  );

  useLayoutEffect(() => {
    const root = rootRef.current;
    const linksRow = linksRowRef.current;
    if (root === null || linksRow === null) return;

    const measure = () => {
      if (
        root.getClientRects().length === 0 ||
        linksRow.getClientRects().length === 0
      ) {
        setPanelLayout(null);
        return;
      }

      const rootRect = root.getBoundingClientRect();
      const linksRect = linksRow.getBoundingClientRect();
      const left = Math.round(
        linksRect.left - rootRect.left - PANEL_LEFT_OVERHANG_PX,
      );
      const nextPanelLayout = {
        left,
        top: Math.round(linksRect.top - rootRect.top - PANEL_TOP_OVERHANG_PX),
        width: Math.round(rootRect.width + PANEL_RIGHT_OVERHANG_PX - left),
      };
      setPanelLayout((current) =>
        panelLayoutsMatch(current, nextPanelLayout) ? current : nextPanelLayout,
      );

      // Opening a panel resizes the root, which re-fires this observer; the
      // panel geometry owns the surface then, so only refresh the link
      // highlight while no panel is open.
      const activeHoveredIndex = hoveredIndexRef.current;
      if (activeHoveredIndex !== null && activePanelRef.current === null) {
        const linkGeometry = getLinkSurfaceGeometry(activeHoveredIndex);
        if (linkGeometry !== null) updateSurfaceGeometry(linkGeometry);
      }
    };

    measure();
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(measure);
    observer.observe(root);
    observer.observe(linksRow);
    Array.from(linksRow.children).forEach((link) => observer.observe(link));
    return () => observer.disconnect();
  }, [getLinkSurfaceGeometry, updateSurfaceGeometry]);

  useLayoutEffect(() => {
    if (collapsed) return;

    if (panel !== null) {
      const panelHeight = panelHeights[panel];
      const activePanelLayout = panelLayout;
      if (activePanelLayout !== null && panelHeight !== undefined) {
        updateSurfaceGeometry({ ...activePanelLayout, height: panelHeight });
      }
      return;
    }

    if (hoveredIndex !== null) {
      const linkGeometry = getLinkSurfaceGeometry(hoveredIndex);
      if (linkGeometry !== null) updateSurfaceGeometry(linkGeometry);
      return;
    }

    // At rest the highlight parks behind the Shop CTA (Figma State=Default),
    // so the first hover slides it out instead of growing it from nothing.
    const ctaElement = shopTriggerRef.current;
    if (ctaElement !== null) {
      const ctaGeometry = getElementSurfaceGeometry(ctaElement);
      if (ctaGeometry !== null) updateSurfaceGeometry(ctaGeometry);
    }
  }, [
    collapsed,
    getElementSurfaceGeometry,
    getLinkSurfaceGeometry,
    hoveredIndex,
    panel,
    panelHeights,
    panelLayout,
    updateSurfaceGeometry,
  ]);

  // Measured against whatever the consumer positioned it in — read from the
  // live offsetParent rather than a second ref, so the chip stays correct
  // wherever it is placed. Parks behind the Shop CTA at rest like the main
  // surface, so the first hover slides out of it.
  useLayoutEffect(() => {
    const origin = linkHighlightRef.current?.offsetParent;
    if (!(origin instanceof HTMLElement)) return;

    const hoveredLink =
      hoveredIndex === null
        ? null
        : linksRowRef.current?.querySelector<HTMLElement>(
            `[data-nav-link-index="${hoveredIndex}"]`,
          );
    const target = hoveredLink ?? shopTriggerRef.current;
    if (target === null || target === undefined) return;

    const nextGeometry = getGeometryWithin(target, origin);
    setLinkHighlightGeometry((current) =>
      geometriesMatch(current, nextGeometry) ? current : nextGeometry,
    );
  }, [collapsed, getGeometryWithin, hoveredIndex, panel, panelLayout]);

  const cancelScheduledClose = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelScheduledClose();
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      updateHoveredIndex(null);
      setPanel(null);
      onDismiss?.();
    }, NAV_CLOSE_DELAY_MS);
  }, [cancelScheduledClose, onDismiss, updateHoveredIndex]);

  useEffect(() => cancelScheduledClose, [cancelScheduledClose]);

  useLayoutEffect(() => {
    activePanelRef.current = panel;
    if (panel !== null) lastPanelRef.current = panel;
  }, [panel]);

  useEffect(() => {
    if (collapsed) {
      cancelScheduledClose();
      setPanel(null);
      updateHoveredIndex(null);
    }
  }, [cancelScheduledClose, collapsed, updateHoveredIndex]);

  const handleNavigate = () => {
    cancelScheduledClose();
    updateHoveredIndex(null);
    setPanel(null);
    onNavigate?.();
  };

  const handlePanelHeightChange = useCallback(
    (panelId: NavHoverPanelId, panelHeight: number) => {
      setPanelHeights((current) =>
        current[panelId] === panelHeight
          ? current
          : { ...current, [panelId]: panelHeight },
      );
    },
    [],
  );

  const getPanelTrigger = (panelId: NavHoverPanelId): HTMLElement | null =>
    panelId === "resources"
      ? resourcesTriggerRef.current
      : shopTriggerRef.current;

  const focusPanelFromTrigger = (
    event: KeyboardEvent<HTMLElement>,
    panelId: NavHoverPanelId,
  ) => {
    if (event.key !== "Tab" || event.shiftKey || panel !== panelId) return;

    const firstLink = Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>("a[href]") ?? [],
    ).find((link) => !link.closest("[inert]"));
    if (firstLink !== undefined) {
      event.preventDefault();
      firstLink.focus();
    }
  };

  /* Hover is not intent: a cursor crossing the row used to report an open for
     every panel it passed. A panel now has to stay open for NAV_DWELL_MS —
     the same beat the collapsed pill already waits before expanding — and the
     pending report is cancelled if the pointer moves on. */
  const reportPanelOpen = useCallback(
    (surface: NavSurface, nextPanel: NavPanel) => {
      window.clearTimeout(panelDwellRef.current);
      panelDwellRef.current = window.setTimeout(() => {
        track("nav_opened", { surface, panel: nextPanel });
      }, NAV_DWELL_MS);
    },
    [track],
  );

  const openShopPanel = () => {
    if (collapsed || Date.now() < collapseSettledAtRef.current) return;

    cancelScheduledClose();
    updateHoveredIndex(null);
    if (panel !== "shop") {
      reportPanelOpen("shop_panel", "shop");
    }
    setPanel("shop");
  };
  // Collapsing used to only mask the panel, so scrolling back up restored the
  // stale open state as if it had been hovered. Clear it instead, and ignore
  // hover that the collapse transition itself produces: SiteNav re-applies
  // focus as the nav swaps between its in-flow and floating forms, and a
  // parked cursor lands on whatever appears beneath it. The floating pill's
  // own expansion is already gated on pointer movement, so its caller opts out.
  useEffect(() => {
    collapseSettledAtRef.current = settleAfterCollapseChange
      ? Date.now() + NAV_SETTLE_MS
      : 0;
    if (collapsed) {
      setPanel(null);
      // Hiding the trigger can swallow its mouseleave; a stale hover would
      // then open the panel on the next expansion from anywhere in the pill.
      shopHoveredRef.current = false;
      return;
    }
    // Expanding can arrive after the cursor has already settled on Shop: the
    // collapsed pill blocks the open, and the enter that would retry it never
    // fires again because the pointer never left. Ask once more on expansion,
    // still through openShopPanel so the settle gate above keeps deciding.
    if (shopHoveredRef.current) {
      openShopPanel();
    }
  }, [collapsed, settleAfterCollapseChange]);

  const activePanel = collapsed ? null : panel;
  const contentPanelLayout = panelLayout;
  const surfaceVisible =
    !collapsed && (activePanel !== null || hoveredIndex !== null);

  const links = (
    <div ref={linksRowRef} className="relative flex items-center">
      {NAV_LINKS.map((link, index) => {
        const triggerProps = {
          "data-nav-control": "menu",
          "data-nav-link-index": index,
          ...(link.panel !== null
            ? {
                "aria-expanded": activePanel === link.panel,
                "aria-haspopup": "true" as const,
              }
            : {}),
          onMouseEnter: (event: MouseEvent<HTMLElement>) => {
            if (collapsed) return;

            cancelScheduledClose();
            updateHoveredIndex(index);
            if (link.panel === null) {
              const linkGeometry = getElementSurfaceGeometry(
                event.currentTarget,
              );
              if (linkGeometry !== null) updateSurfaceGeometry(linkGeometry);
            }
            if (
              link.panel !== null &&
              Date.now() < collapseSettledAtRef.current
            ) {
              return;
            }
            if (link.panel !== null && link.panel !== panel) {
              reportPanelOpen(
                (link.panel as NavHoverPanelId) === "shop"
                  ? "shop_panel"
                  : "hover_panel",
                link.panel,
              );
            }
            setPanel(link.panel);
          },
          onFocus: () => {
            if (collapsed) return;

            cancelScheduledClose();
            updateHoveredIndex(null);
            setPanel(link.panel);
          },
          onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
            if (link.panel !== null) {
              focusPanelFromTrigger(event, link.panel);
            }
          },
          // Bold stays: on this surface it is the whole hover state. The
          // white highlight is only rendered by FloatingNavSurface, and
          // --color-brand-black and --color-content-primary are both #1f2022,
          // so hovering an in-flow link changes no colour either.
          //
          // What is gone is the reflow. The label is duplicated at semibold in
          // an ::after that is hidden and zero-height, so every link is always
          // as wide as its own bold text and the row stops shuffling — it used
          // to push every link left of the hovered one by up to 1.9px.
          className: cn(
            "relative rounded-full px-l py-m font-ppMori text-paragraph-sm transition-colors motion-reduce:transition-none",
            "after:invisible after:block after:h-0 after:overflow-hidden after:whitespace-nowrap after:font-semibold after:content-[attr(data-label)]",
            hoveredIndex === index
              ? "font-semibold text-brand-black"
              : hoveredIndex === null && activePanel === null
                ? "font-normal text-content-primary"
                : "font-normal text-content-secondary",
          ),
          "data-label": link.label,
          // Same 200ms as the highlight behind it (surfaceTransition), so the
          // two halves of the hover state land together.
          style: {
            transitionDuration: `${MotionDuration.quickMs}ms`,
            transitionTimingFunction: NAV_PANEL_TRIGGER_EASE_CSS,
          },
        };

        return link.href === null ? (
          <button
            key={link.label}
            ref={resourcesTriggerRef}
            type="button"
            {...triggerProps}
          >
            {link.label}
          </button>
        ) : (
          <Link
            key={link.label}
            href={link.href}
            onClick={() => {
              track("nav_link_clicked", {
                href: link.href,
                label: link.label,
                surface: collapsed ? "collapsed_pill" : "top_bar",
                nav_link_index: index,
              });
              handleNavigate();
            }}
            {...triggerProps}
          >
            {link.label}
          </Link>
        );
      })}
    </div>
  );

  // Figma draws the highlight white on the nav's #f2f2f0 field (Desktop Nav
  // 5299:1536 Highlight over BG). Surfaces that kept a container made it solid
  // white, so the pairing inverts there: the same one step of contrast, taken
  // out of the field colour instead. No shadow — the Figma node carries none,
  // and inside a container it would read as a dent rather than a lift.
  const linkHighlight = (
    <motion.div
      ref={linkHighlightRef}
      aria-hidden
      data-nav-link-highlight
      initial={false}
      animate={{
        ...linkHighlightGeometry,
        opacity:
          !collapsed && hoveredIndex !== null && activePanel === null ? 1 : 0,
      }}
      transition={surfaceTransition}
      className="pointer-events-none absolute rounded-l bg-brand-grey"
    />
  );

  const rightControls = ({
    cartCount,
    onCartClick,
  }: DesktopNavRightControlsOptions) => (
    <DesktopNavRightControls
      cartCount={cartCount}
      onCartClick={onCartClick}
      panel={activePanel}
      shopTriggerRef={shopTriggerRef}
      onShopEnter={() => {
        shopHoveredRef.current = true;
        openShopPanel();
      }}
      onShopLeave={() => {
        shopHoveredRef.current = false;
      }}
      onShopKeyDown={(event) => focusPanelFromTrigger(event, "shop")}
      onNavigate={handleNavigate}
      onCartEnter={() => {
        updateHoveredIndex(null);
        setPanel(null);
      }}
      onControlsFocus={(event) => {
        if (!shopTriggerRef.current?.contains(event.target as Node)) {
          setPanel(null);
        }
      }}
    />
  );

  return (
    // While a panel is open everything sits on the grown white surface, so the
    // subtree re-scopes to the light theme — on dark pages (Innovation) the dark-theme
    // white text would otherwise vanish against the white panel.
    // The key handler here manages Tab containment for the open panel; the
    // element is a container, not a control.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={setRootRef}
      data-theme={activePanel !== null ? "light" : undefined}
      className="relative z-30"
      onBlur={(event) => {
        const nextTarget = event.relatedTarget;
        if (
          !(nextTarget instanceof Node) ||
          !event.currentTarget.contains(nextTarget)
        ) {
          setPanel(null);
        }
      }}
      onKeyDown={(event) => {
        if (
          event.key === "Tab" &&
          panel !== null &&
          panelRef.current?.contains(event.target as Node)
        ) {
          const panelLinks = Array.from(
            panelRef.current.querySelectorAll<HTMLAnchorElement>("a[href]"),
          ).filter((link) => !link.closest("[inert]"));
          const firstLink = panelLinks[0];
          const lastLink = panelLinks[panelLinks.length - 1];

          if (event.shiftKey && event.target === firstLink) {
            event.preventDefault();
            getPanelTrigger(panel)?.focus();
          } else if (!event.shiftKey && event.target === lastLink) {
            event.preventDefault();
            if (panel === "resources") {
              shopTriggerRef.current?.focus();
            } else {
              const cartControl =
                event.currentTarget.querySelector<HTMLElement>(
                  "[data-nav-control='cart']",
                );
              cartControl?.focus();
            }
          }
          return;
        }

        if (event.key !== "Escape") return;

        if (panel !== null) {
          event.preventDefault();
          event.stopPropagation();
          const trigger = getPanelTrigger(panel);
          trigger?.focus();
          setPanel(null);
          return;
        }

        if (onEscape !== undefined) {
          event.preventDefault();
          event.stopPropagation();
          onEscape();
        }
      }}
      onMouseEnter={cancelScheduledClose}
      onMouseLeave={() => {
        updateHoveredIndex(null);
        scheduleClose();
      }}
    >
      <motion.div
        aria-hidden
        data-nav-hover-surface
        initial={false}
        animate={{
          ...surfaceGeometry,
          opacity: surfaceVisible ? 1 : 0,
        }}
        transition={surfaceTransition}
        className="pointer-events-none absolute rounded-l bg-brand-white shadow-btnShadow"
      />
      {children({ links, rightControls, linkHighlight })}
      <NavHoverPanel
        panel={activePanel}
        layout={contentPanelLayout}
        onNavigate={handleNavigate}
        onMouseEnter={() => updateHoveredIndex(null)}
        onPanelHeightChange={handlePanelHeightChange}
        containerRef={panelRef}
      />
    </div>
  );
};

type DesktopNavProps = {
  cartCount: number;
  onCartClick: () => void;
  onNavigate?: (() => void) | undefined;
  collapsed?: boolean;
};

const DesktopNav = ({
  cartCount,
  onCartClick,
  onNavigate,
  collapsed = false,
}: DesktopNavProps) => (
  <DesktopNavLinks
    onNavigate={onNavigate}
    collapsed={collapsed}
    settleAfterCollapseChange
  >
    {({ links, rightControls }) => (
      // Must outrank NavHoverPanel's z-[5], or its overhang swallows link hovers.
      <div className="relative z-10 flex items-center gap-m rounded-full p-1">
        {links}
        {rightControls({ cartCount, onCartClick })}
      </div>
    )}
  </DesktopNavLinks>
);

export default DesktopNav;
